/**
 * @voltxampere/audio — Damage sound effects (Sprint 37-1)
 *
 * 8 destructive-event sound effects synthesised via Tone.js. Each
 * sound is built from primitives (oscillators / noise + filters +
 * envelopes) — NO sample files (asset hafifliği prensibi; ZENIT
 * brand "minimum dependencies").
 *
 * Sound roster:
 *   1. led-pop                — high pop (LED stage 3 frame 0)
 *   2. capacitor-bang         — low boom (capacitor stage 1 frame 0)
 *   3. resistor-sizzle        — sustained crackle (during burn anim)
 *   4. wire-crackle           — short bursts (wire melt stage 2-3)
 *   5. smoke-hiss             — sustained soft "shh" (smoke trail)
 *   6. spark-zap              — quick electric zap (LED sparks sync)
 *   7. capacitor-swell-creak  — slow plastic-stretch (cap stage 0)
 *   8. cooldown-crackle       — sparse metallic clicks (post-event)
 *
 * Atmospheric depth: every sound routes through one shared Reverb
 * bus (decay 1.2s, wet 0.25). Mirrors the ZENIT "observatory" brand
 * — sounds resonate inside an empty observatory.
 *
 * Lifecycle:
 *   await initDamageSounds();         // user-gesture init
 *   playDamageSound("led-pop");       // one-shot
 *   playDamageSound("resistor-sizzle"); // start sustained
 *   stopDamageSound("resistor-sizzle"); // stop sustained
 *   disposeDamageSounds();            // unmount cleanup
 *
 * Tone.js dynamic import (mirrors `engines/tone-engine.ts` pattern):
 * module-level `import * as Tone from "tone"` causes jsdom test
 * environments to fail at module-resolution time. Dynamic import keeps
 * the audio package importable from Node-only contexts.
 *
 * Engine zero-diff invariant honored — this module imports nothing
 * from `@voltxampere/engine`.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/** All 8 damage sound IDs (frozen for iteration). */
export const ALL_DAMAGE_SOUND_IDS = [
    "led-pop",
    "capacitor-bang",
    "resistor-sizzle",
    "wire-crackle",
    "smoke-hiss",
    "spark-zap",
    "capacitor-swell-creak",
    "cooldown-crackle",
];
const sounds = new Map();
let initialized = false;
let initPromise = null;
/** Master gain for damage-sound bus (separate from Sprint 31 master). */
export const DAMAGE_BUS_GAIN = 0.6;
/** Shared reverb decay (seconds). */
export const REVERB_DECAY_S = 1.2;
/** Shared reverb pre-delay (seconds). */
export const REVERB_PREDELAY_S = 0.02;
/** Shared reverb wet mix (0..1). */
export const REVERB_WET = 0.25;
/** Sustained sizzle peak gain (0..1). */
export const RESISTOR_SIZZLE_PEAK_GAIN = 0.3;
/** Sustained smoke hiss peak gain (0..1). */
export const SMOKE_HISS_PEAK_GAIN = 0.15;
/**
 * Lazy initialise all 8 damage sound synthesizers.
 *
 * MUST be called from a user-gesture handler (browser autoplay policy
 * — Chrome/Safari/Firefox all require an interaction before
 * AudioContext.resume()). Idempotent: subsequent calls return the
 * same Promise.
 */
export async function initDamageSounds() {
    if (initialized)
        return;
    if (initPromise) {
        await initPromise;
        return;
    }
    initPromise = (async () => {
        const Tone = (await import("tone"));
        await Tone.start();
        // Master damage bus → destination.
        const damageGain = new Tone.Gain(DAMAGE_BUS_GAIN).toDestination();
        // Shared atmospheric reverb (observatory hissi).
        const reverb = new Tone.Reverb({
            decay: REVERB_DECAY_S,
            preDelay: REVERB_PREDELAY_S,
            wet: REVERB_WET,
        });
        reverb.connect(damageGain);
        registerLedPop(Tone, reverb);
        registerCapacitorBang(Tone, reverb);
        registerResistorSizzle(Tone, reverb);
        registerWireCrackle(Tone, reverb);
        registerSmokeHiss(Tone, reverb);
        registerSparkZap(Tone, reverb);
        registerCapacitorSwellCreak(Tone, reverb);
        registerCooldownCrackle(Tone, reverb);
        initialized = true;
    })();
    await initPromise;
}
/**
 * Trigger a damage sound. No-op when the sound id is unknown OR the
 * module has not been initialised yet (call sites should preflight
 * via `isDamageSoundsInitialized()` if they want to gate ahead of
 * time).
 *
 * `when` is an optional Tone.js absolute time (e.g. `"+0.02"` for
 * 20 ms in the future). Forward-compat for sample-accurate dispatch.
 */
export function playDamageSound(id, when) {
    const sound = sounds.get(id);
    if (!sound)
        return;
    sound.play(when);
}
/**
 * Stop a sustained sound (sizzle, hiss). One-shot sounds (pop, bang,
 * etc.) ignore stop() because they decay naturally. Unknown id is a
 * silent no-op.
 */
export function stopDamageSound(id) {
    const sound = sounds.get(id);
    if (!sound?.stop)
        return;
    sound.stop();
}
/**
 * Dispose every Tone.js node owned by the module. Resets the
 * initialised flag so subsequent `initDamageSounds()` works again.
 *
 * Apps/web does NOT call this in normal lifecycle — the module
 * lives for the page's lifetime. Tests + HMR reload paths are the
 * primary callers.
 */
export function disposeDamageSounds() {
    for (const sound of sounds.values()) {
        sound.cleanup();
    }
    sounds.clear();
    initialized = false;
    initPromise = null;
}
/** True iff `initDamageSounds()` has resolved at least once. */
export function isDamageSoundsInitialized() {
    return initialized;
}
// ──────────────────────────────────────────────────────────────────
// Per-sound registration helpers
// ──────────────────────────────────────────────────────────────────
/** 1. LED pop — high tic, fast decay. */
function registerLedPop(Tone, reverb) {
    const synth = new Tone.MembraneSynth({
        pitchDecay: 0.008,
        octaves: 2,
        envelope: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.02 },
    });
    synth.connect(reverb);
    sounds.set("led-pop", {
        id: "led-pop",
        play: (when) => {
            synth.triggerAttackRelease("C5", "32n", when);
        },
        cleanup: () => {
            synth.dispose();
        },
    });
}
/** 2. Capacitor bang — low frequency boom. */
function registerCapacitorBang(Tone, reverb) {
    const synth = new Tone.MembraneSynth({
        pitchDecay: 0.05,
        octaves: 4,
        envelope: { attack: 0.002, decay: 0.4, sustain: 0, release: 0.3 },
    });
    synth.connect(reverb);
    sounds.set("capacitor-bang", {
        id: "capacitor-bang",
        play: (when) => {
            synth.triggerAttackRelease("C2", "8n", when);
        },
        cleanup: () => {
            synth.dispose();
        },
    });
}
/** 3. Resistor sizzle — sustained white noise + bandpass. */
function registerResistorSizzle(Tone, reverb) {
    const noise = new Tone.Noise("white");
    const filter = new Tone.Filter(2000, "bandpass");
    const gain = new Tone.Gain(0);
    noise.connect(gain);
    gain.connect(filter);
    filter.connect(reverb);
    noise.start();
    sounds.set("resistor-sizzle", {
        id: "resistor-sizzle",
        play: () => {
            gain.gain.rampTo(RESISTOR_SIZZLE_PEAK_GAIN, 0.1);
        },
        stop: () => {
            gain.gain.rampTo(0, 0.2);
        },
        cleanup: () => {
            noise.stop();
            noise.dispose();
            filter.dispose();
            gain.dispose();
        },
    });
}
/** 4. Wire crackle — short pink-noise bursts via amplitude envelope. */
function registerWireCrackle(Tone, reverb) {
    const noise = new Tone.Noise("pink");
    const filter = new Tone.Filter(4000, "highpass");
    const env = new Tone.AmplitudeEnvelope({
        attack: 0.005,
        decay: 0.08,
        sustain: 0,
        release: 0.05,
    });
    noise.connect(env);
    env.connect(filter);
    filter.connect(reverb);
    noise.start();
    sounds.set("wire-crackle", {
        id: "wire-crackle",
        play: (when) => {
            env.triggerAttackRelease("16n", when);
        },
        cleanup: () => {
            noise.stop();
            noise.dispose();
            filter.dispose();
            env.dispose();
        },
    });
}
/** 5. Smoke hiss — sustained brown noise + lowpass. */
function registerSmokeHiss(Tone, reverb) {
    const noise = new Tone.Noise("brown");
    const filter = new Tone.Filter(800, "lowpass");
    const gain = new Tone.Gain(0);
    noise.connect(gain);
    gain.connect(filter);
    filter.connect(reverb);
    noise.start();
    sounds.set("smoke-hiss", {
        id: "smoke-hiss",
        play: () => {
            gain.gain.rampTo(SMOKE_HISS_PEAK_GAIN, 0.5);
        },
        stop: () => {
            gain.gain.rampTo(0, 1.0);
        },
        cleanup: () => {
            noise.stop();
            noise.dispose();
            filter.dispose();
            gain.dispose();
        },
    });
}
/** 6. Spark zap — quick sawtooth burst, randomised pitch. */
function registerSparkZap(Tone, reverb) {
    const osc = new Tone.Oscillator(800, "sawtooth");
    const filter = new Tone.Filter(3000, "bandpass");
    const env = new Tone.AmplitudeEnvelope({
        attack: 0.001,
        decay: 0.04,
        sustain: 0,
        release: 0.02,
    });
    osc.connect(env);
    env.connect(filter);
    filter.connect(reverb);
    osc.start();
    sounds.set("spark-zap", {
        id: "spark-zap",
        play: (when) => {
            osc.frequency.setValueAtTime(800 + Math.random() * 400, when ?? 0);
            env.triggerAttackRelease("32n", when);
        },
        cleanup: () => {
            osc.stop();
            osc.dispose();
            filter.dispose();
            env.dispose();
        },
    });
}
/** 7. Capacitor swell creak — slow rising sine, plastic-stretch feel. */
function registerCapacitorSwellCreak(Tone, reverb) {
    const osc = new Tone.Oscillator(80, "sine");
    const filter = new Tone.Filter(300, "lowpass");
    const env = new Tone.AmplitudeEnvelope({
        attack: 0.15,
        decay: 0.1,
        sustain: 0.2,
        release: 0.4,
    });
    osc.connect(env);
    env.connect(filter);
    filter.connect(reverb);
    osc.start();
    sounds.set("capacitor-swell-creak", {
        id: "capacitor-swell-creak",
        play: (when) => {
            osc.frequency.rampTo(140, 0.3, when);
            env.triggerAttackRelease("4n", when);
        },
        cleanup: () => {
            osc.stop();
            osc.dispose();
            filter.dispose();
            env.dispose();
        },
    });
}
/** 8. Cooldown crackle — sparse metallic clicks (post-explosion). */
function registerCooldownCrackle(Tone, reverb) {
    const noise = new Tone.Noise("pink");
    const filter = new Tone.Filter(6000, "highpass");
    const env = new Tone.AmplitudeEnvelope({
        attack: 0.001,
        decay: 0.02,
        sustain: 0,
        release: 0.01,
    });
    noise.connect(env);
    env.connect(filter);
    filter.connect(reverb);
    noise.start();
    sounds.set("cooldown-crackle", {
        id: "cooldown-crackle",
        play: (when) => {
            env.triggerAttackRelease("64n", when);
        },
        cleanup: () => {
            noise.stop();
            noise.dispose();
            filter.dispose();
            env.dispose();
        },
    });
}
/** Test-only — clear module state without disposing (jsdom safe). */
export function __resetDamageSoundsForTests() {
    sounds.clear();
    initialized = false;
    initPromise = null;
}
//# sourceMappingURL=damage-sounds.js.map